import React, { useState, useMemo } from 'react';
import {
  Users,
  UserCheck,
  TrendingDown,
  TrendingUp,
  Image,
  Video,
  AlertTriangle,
  Target,
  Briefcase,
  Globe,
  Palette,
  GraduationCap,
  CalendarDays,
  ArrowRight,
  BarChart3,
  Activity,
  CheckCircle2,
  SkipForward,
  XCircle,
  UserX,
  Sparkles,
  Instagram,
  UserPlus
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

// ============================================
// CORES
// ============================================

const PLAN_COLORS = {
  // Pagantes ativos
  Max: '#f59e0b',
  Pro: '#8b5cf6',
  Basic: '#3b82f6',
  // Pagamento problematico mas ainda ativo
  'Pagamento em atraso': '#eab308',
  // Ex-pagantes / nunca pagou (separados para evitar inflar "Free")
  Cancelado: '#ef4444',
  'Trial expirado': '#f97316',
  'Nunca assinou': '#9ca3af',
  // Compat legado
  Free: '#9ca3af'
};

const CHART_COLORS = ['#f59e0b', '#3b82f6', '#8b5cf6', '#10b981', '#ef4444', '#06b6d4', '#f97316', '#ec4899', '#84cc16', '#6366f1'];

// ============================================
// TOOLTIP CUSTOMIZADO
// ============================================

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-lg border border-gray-700">
      <p className="font-medium">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color || '#f59e0b' }}>
          {entry.name || 'Quantidade'}: {entry.value}
        </p>
      ))}
    </div>
  );
};

const renderPieLabel = ({ name, percent }) => {
  if (percent < 0.05) return null;
  return `${name} ${(percent * 100).toFixed(0)}%`;
};

// ============================================
// SKELETON LOADING
// ============================================

const SkeletonCard = () => (
  <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
    <div className="h-8 bg-gray-200 rounded w-1/3" />
  </div>
);

const SkeletonChart = ({ height = 350 }) => (
  <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm animate-pulse">
    <div className="h-5 bg-gray-200 rounded w-1/2 mb-2" />
    <div className="h-3 bg-gray-100 rounded w-2/3 mb-4" />
    <div className="bg-gray-100 rounded" style={{ height }} />
  </div>
);

// ============================================
// CARD DE SECAO (titulo em pergunta + subtitulo)
// ============================================

const SectionCard = ({ question, description, children, className = '' }) => (
  <div className={`bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden ${className}`}>
    <div className="px-6 pt-6 pb-4">
      <h3 className="text-lg font-semibold text-gray-900">{question}</h3>
      {description && (
        <p className="text-sm text-gray-400 mt-1">{description}</p>
      )}
    </div>
    <div className="px-6 pb-6">
      {children}
    </div>
  </div>
);

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const UsersAnalyticsDashboard = ({ data, isLoading }) => {
  const [periodType, setPeriodType] = useState('monthly');

  // ── Dados de signups por período selecionado ──
  const signupsData = useMemo(() => {
    const signups_by_date = data?.signups_by_date;
    if (periodType === 'weekly') return (signups_by_date?.weekly || []).map(d => ({ name: d.week, count: d.count }));
    if (periodType === 'monthly') return (signups_by_date?.monthly || []).map(d => ({ name: d.month, count: d.count }));
    return (signups_by_date?.daily || []).map(d => ({ name: d.date, count: d.count }));
  }, [periodType, data?.signups_by_date]);

  // Total de signups no período visível
  const totalSignupsInView = signupsData.reduce((sum, d) => sum + d.count, 0);

  // Período label
  const periodLabel = periodType === 'daily' ? 'por dia' : periodType === 'weekly' ? 'por semana' : 'por mês';

  // ── Loading state ──
  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <SkeletonChart height={60} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
        <SkeletonChart />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SkeletonChart height={300} />
          <SkeletonChart height={300} />
        </div>
        <SkeletonChart />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SkeletonChart height={280} />
          <SkeletonChart height={280} />
        </div>
      </div>
    );
  }

  const {
    kpi_summary, signups_by_date, plan_distribution, churn_analysis,
    posts_before_churn, onboarding_insights, user_activity,
    onboarding_funnel, content_metrics, free_generation_funnel
  } = data;

  // Dados do funil de onboarding para o grafico
  const onboardingFunnelData = onboarding_funnel ? [
    { name: 'Completou', count: onboarding_funnel.completed, color: '#10b981' },
    { name: 'Pulou', count: onboarding_funnel.skipped, color: '#f59e0b' },
    { name: 'Abandonou', count: onboarding_funnel.abandoned, color: '#ef4444' },
    { name: 'Nunca iniciou', count: onboarding_funnel.never_started, color: '#9ca3af' }
  ] : [];

  // Cores para atividade
  const ACTIVITY_COLORS = {
    'Hoje': '#10b981',
    'Últimos 7 dias': '#3b82f6',
    'Últimos 30 dias': '#f59e0b',
    'Mais de 30 dias': '#ef4444',
    'Nunca logou': '#9ca3af'
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="space-y-8">

      {/* ══════════════════════════════════════════
          0. SUMARIO EXECUTIVO (dinamico)
          ══════════════════════════════════════════ */}
      <ExecutiveSummary
        kpi={kpi_summary}
        planDistribution={plan_distribution}
        userActivity={user_activity}
        churn={churn_analysis}
        onboardingFunnel={onboarding_funnel}
        postsBeforeChurn={posts_before_churn}
      />

      {/* ══════════════════════════════════════════
          1. SELETOR DE PERIODO
          ══════════════════════════════════════════ */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-100 rounded-xl">
              <CalendarDays className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-900">Como você quer visualizar?</p>
              <p className="text-xs text-amber-600">Escolha o período para analisar a evolução dos seus leads</p>
            </div>
          </div>
          <div className="flex gap-1 bg-white rounded-xl p-1.5 shadow-sm border border-amber-200">
            {[
              { key: 'daily', label: 'Por dia', icon: CalendarDays },
              { key: 'weekly', label: 'Por semana', icon: BarChart3 },
              { key: 'monthly', label: 'Por mês', icon: TrendingUp }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setPeriodType(key)}
                className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${
                  periodType === key
                    ? 'bg-amber-500 text-white shadow-md'
                    : 'text-gray-500 hover:bg-amber-50 hover:text-amber-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          2. KPI CARDS - VISAO GERAL
          ══════════════════════════════════════════ */}
      <div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">Qual é o cenário atual?</h3>
        <p className="text-sm text-gray-400 mb-4">Seus números mais importantes em tempo real</p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <KpiCard
            icon={Users}
            question="Total usuários"
            value={kpi_summary?.total_users || 0}
            color="text-gray-700"
            bgColor="bg-gray-50"
            borderColor="border-gray-200"
          />
          <KpiCard
            icon={UserCheck}
            question="Pagantes ativos"
            value={kpi_summary?.paid_users ?? 0}
            suffix={kpi_summary?.payment_overdue_users
              ? `+${kpi_summary.payment_overdue_users} em atraso`
              : (kpi_summary?.total_users > 0 ? `de ${kpi_summary.total_users}` : '')}
            color="text-green-600"
            bgColor="bg-green-50"
            borderColor="border-green-200"
          />
          <KpiCard
            icon={TrendingDown}
            question="Cancelaram"
            value={kpi_summary?.churned_users || 0}
            suffix={kpi_summary?.churn_rate > 0 ? `${kpi_summary.churn_rate}% churn` : ''}
            color="text-red-600"
            bgColor="bg-red-50"
            borderColor="border-red-200"
          />
          <KpiCard
            icon={Target}
            question="Conversão paga"
            value={`${kpi_summary?.conversion_rate || 0}%`}
            suffix="do Free"
            color="text-blue-600"
            bgColor="bg-blue-50"
            borderColor="border-blue-200"
          />
          <KpiCard
            icon={CheckCircle2}
            question="Onboarding"
            value={`${kpi_summary?.onboarding_completion_rate || 0}%`}
            suffix="completaram"
            color="text-emerald-600"
            bgColor="bg-emerald-50"
            borderColor="border-emerald-200"
          />
          <KpiCard
            icon={Sparkles}
            question="Posts gerados"
            value={kpi_summary?.total_contents || 0}
            suffix={`em ${kpi_summary?.total_batches || 0} batches`}
            color="text-purple-600"
            bgColor="bg-purple-50"
            borderColor="border-purple-200"
          />
        </div>
      </div>

      {/* ══════════════════════════════════════════
          3. FUNIL DE GERACAO GRATUITA (INSTAGRAM)
          ══════════════════════════════════════════ */}
      {free_generation_funnel && (
        <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl p-5 border border-pink-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-pink-100 rounded-xl">
              <Instagram className="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-800">Quantas pessoas testaram o gerador gratuito?</h3>
              <p className="text-sm text-gray-400">Visitantes que usaram a geração gratuita via Instagram na landing page</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-pink-100">
              <p className="text-xs text-gray-400 font-medium mb-1">Gerações gratuitas</p>
              <p className="text-2xl font-bold text-pink-600">{free_generation_funnel.total_free_generations}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-purple-100">
              <p className="text-xs text-gray-400 font-medium mb-1">Perfis únicos (@)</p>
              <p className="text-2xl font-bold text-purple-600">{free_generation_funnel.unique_instagram_users}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-blue-100">
              <p className="text-xs text-gray-400 font-medium mb-1">Usuários registrados</p>
              <p className="text-2xl font-bold text-blue-600">{free_generation_funnel.total_registered_users}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-green-100">
              <p className="text-xs text-gray-400 font-medium mb-1">Conversão estimada</p>
              <p className="text-2xl font-bold text-green-600">{free_generation_funnel.estimated_conversion_rate}%</p>
              <p className="text-xs text-gray-300 mt-0.5">registros / perfis únicos</p>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          4. SIGNUPS POR PERIODO
          ══════════════════════════════════════════ */}
      <SectionCard
        question={`Quantos leads estão entrando ${periodLabel}?`}
        description={signupsData.length > 0
          ? `${totalSignupsInView} leads no total ao longo de ${signupsData.length} ${periodType === 'daily' ? 'dias' : periodType === 'weekly' ? 'semanas' : 'meses'}`
          : 'Nenhum dado registrado ainda'
        }
      >
        {signupsData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={signupsData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickFormatter={(v) => {
                  if (periodType === 'daily' && v.length === 10) return v.slice(5);
                  if (periodType === 'monthly' && v.length === 7) return v.slice(2);
                  return v;
                }}
              />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Novos leads" fill="#f59e0b" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState text="Ainda não há registros de leads para este período" />
        )}
      </SectionCard>

      {/* ══════════════════════════════════════════
          5. ATIVIDADE DOS USUARIOS + 6. FUNIL DE ONBOARDING
          ══════════════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Atividade - Ultimo login */}
        <SectionCard
          question="Os usuários estão usando a plataforma?"
          description="Última atividade detectada (login ou geração de conteúdo). 'Nunca logou' inclui contas criadas sem nenhuma ação posterior."
        >
          {user_activity?.last_login_distribution?.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={user_activity.last_login_distribution}
                  layout="vertical"
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} width={110} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Usuários" radius={[0, 6, 6, 0]}>
                    {user_activity.last_login_distribution.map((entry, i) => (
                      <Cell key={i} fill={ACTIVITY_COLORS[entry.name] || '#9ca3af'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {(() => {
                const neverLogged = user_activity.last_login_distribution.find(d => d.name === 'Nunca logou')?.count || 0;
                const total = user_activity.last_login_distribution.reduce((s, d) => s + d.count, 0);
                if (total > 0 && neverLogged > total * 0.5) {
                  return (
                    <div className="mt-3 bg-amber-50 border border-amber-100 rounded-xl p-3 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-700">
                        {Math.round(neverLogged / total * 100)}% dos usuários nunca fizeram login após o cadastro. Considere implementar emails de boas-vindas ou notificações.
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
            </>
          ) : (
            <EmptyState text="Sem dados de atividade" />
          )}
        </SectionCard>

        {/* Funil de Onboarding */}
        <SectionCard
          question="Quantos completaram o onboarding?"
          description="Usuários que passaram pelo processo de configuração inicial"
        >
          {onboardingFunnelData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={onboardingFunnelData}
                  layout="vertical"
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} width={110} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Usuários" radius={[0, 6, 6, 0]}>
                    {onboardingFunnelData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {onboardingFunnelData.map(d => (
                  <div key={d.name} className="flex items-center gap-2 text-xs">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
                    <span className="text-gray-500">{d.name}:</span>
                    <span className="font-medium text-gray-700">{d.count}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <EmptyState text="Sem dados de onboarding" />
          )}
        </SectionCard>
      </div>

      {/* ══════════════════════════════════════════
          7. METRICAS DE GERACAO DE POSTS
          ══════════════════════════════════════════ */}
      {content_metrics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Por modelo */}
          <SectionCard
            question="Quais modelos de IA estão sendo mais usados?"
            description={`${content_metrics.total_contents} conteúdos gerados em ${content_metrics.total_batches} batches`}
          >
            {content_metrics.by_model?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={content_metrics.by_model}
                  layout="vertical"
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    width={120}
                    tickFormatter={(v) => v.length > 18 ? v.slice(0, 18) + '...' : v}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Conteúdos" fill="#8b5cf6" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState text="Sem dados de geração" />
            )}
          </SectionCard>

          {/* Por tipo (imagem vs video) */}
          <SectionCard
            question="Quanto é imagem e quanto é vídeo?"
            description="Distribuição do tipo de conteúdo gerado na plataforma"
          >
            {content_metrics.by_type?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={content_metrics.by_type}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={80}
                      dataKey="count"
                      nameKey="name"
                      label={renderPieLabel}
                      labelLine={false}
                    >
                      {content_metrics.by_type.map((_, i) => (
                        <Cell key={i} fill={i === 0 ? '#8b5cf6' : '#3b82f6'} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend formatter={(value) => <span className="text-xs text-gray-600">{value === 'image' ? 'Imagens' : 'Vídeos'}</span>} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-100 text-center">
                    <Image className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                    <p className="text-lg font-bold text-purple-700">{content_metrics.by_type.find(t => t.name === 'image')?.count || 0}</p>
                    <p className="text-xs text-purple-400">imagens</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 text-center">
                    <Video className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                    <p className="text-lg font-bold text-blue-700">{content_metrics.by_type.find(t => t.name === 'video')?.count || 0}</p>
                    <p className="text-xs text-blue-400">vídeos</p>
                  </div>
                </div>
              </>
            ) : (
              <EmptyState text="Sem dados de conteúdo" />
            )}
          </SectionCard>
        </div>
      )}

      {/* ══════════════════════════════════════════
          8. PLANOS + CHURN - LADO A LADO
          ══════════════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        <SectionCard
          question="Como está a base de usuários por plano?"
          description="Pagantes ativos (Basic/Pro/Max), ex-pagantes (Cancelado / Trial expirado) e quem nunca chegou a assinar"
        >
          {plan_distribution && plan_distribution.some(d => d.value > 0) ? (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={plan_distribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={95}
                    dataKey="value"
                    label={renderPieLabel}
                    labelLine={false}
                  >
                    {plan_distribution.map((entry) => (
                      <Cell key={entry.name} fill={PLAN_COLORS[entry.name] || '#9ca3af'} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend formatter={(value) => <span className="text-sm text-gray-600">{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-2">
                {plan_distribution.filter(d => d.value > 0).map(d => {
                  const total = plan_distribution.reduce((s, p) => s + p.value, 0);
                  const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
                  return (
                    <div key={d.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PLAN_COLORS[d.name] }} />
                        <span className="text-gray-600">{d.name}</span>
                      </div>
                      <span className="font-medium text-gray-800">{d.value} usuários ({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <EmptyState text="Nenhum dado de planos disponível" />
          )}
        </SectionCard>

        <SectionCard
          question="Por que os usuários estão saindo?"
          description="Veja de quais planos os cancelamentos estão vindo e o uso médio antes de sair"
        >
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-red-50 rounded-xl p-4 text-center border border-red-100">
              <p className="text-3xl font-bold text-red-600">{churn_analysis?.total_churned || 0}</p>
              <p className="text-xs text-red-400 mt-1">Já cancelaram</p>
            </div>
            <div className="bg-orange-50 rounded-xl p-4 text-center border border-orange-100">
              <p className="text-3xl font-bold text-orange-500">{churn_analysis?.about_to_churn || 0}</p>
              <p className="text-xs text-orange-400 mt-1">Prestes a cancelar</p>
            </div>
          </div>

          {(() => {
            // Ao cancelar, o Stripe webhook zera o plan -> todo cancelado vira "Free".
            // Se so existe Free com valor, o grafico por plano e ruido - esconder.
            const nonFreeChurn = (churn_analysis?.churned_by_plan || [])
              .filter(p => p.plan !== 'Free' && p.count > 0);
            if (nonFreeChurn.length === 0) return null;
            return (
            <>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Cancelamentos por plano (último registrado)</p>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart
                  data={nonFreeChurn}
                  layout="vertical"
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
                  <YAxis type="category" dataKey="plan" tick={{ fontSize: 12, fill: '#6b7280' }} width={55} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Cancelados" fill="#ef4444" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </>
          );
          })()}

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
              <div className="flex items-center gap-2 mb-1">
                <Image className="w-3.5 h-3.5 text-gray-400" />
                <p className="text-xs text-gray-400">Média de imagens antes de sair</p>
              </div>
              <p className="text-xl font-bold text-gray-700">{churn_analysis?.avg_usage_before_churn?.images || 0}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
              <div className="flex items-center gap-2 mb-1">
                <Video className="w-3.5 h-3.5 text-gray-400" />
                <p className="text-xs text-gray-400">Média de vídeos antes de sair</p>
              </div>
              <p className="text-xl font-bold text-gray-700">{churn_analysis?.avg_usage_before_churn?.videos || 0}</p>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* ══════════════════════════════════════════
          9. POSTS ANTES DE SAIR
          ══════════════════════════════════════════ */}
      <SectionCard
        question="Quantos posts os usuários geram antes de sair?"
        description="Isso mostra se os usuários estão conseguindo extrair valor da plataforma. Se a maioria sai com poucos posts, pode indicar dificuldade no uso ou expectativas não atendidas."
      >
        {posts_before_churn?.histogram?.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={posts_before_churn.histogram} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="range"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  label={{ value: 'Quantidade de posts gerados', position: 'insideBottom', offset: -2, fontSize: 11, fill: '#9ca3af' }}
                />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Usuários que saíram" fill="#ef4444" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>

            {(() => {
              const hist = posts_before_churn.histogram;
              const zeroCount = hist.find(h => h.range === '0')?.count || 0;
              const lowCount = (hist.find(h => h.range === '1-5')?.count || 0) + zeroCount;
              const total = hist.reduce((s, h) => s + h.count, 0);
              if (total === 0) return null;
              const lowPct = Math.round((lowCount / total) * 100);
              if (lowPct > 50) {
                return (
                  <div className="mt-4 bg-red-50 border border-red-100 rounded-xl p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-700">
                        {lowPct}% dos usuários que saíram geraram 5 posts ou menos
                      </p>
                      <p className="text-xs text-red-400 mt-1">
                        Isso pode indicar que os usuários não estão conseguindo usar a plataforma ou não estão vendo valor rápido o suficiente. Considere melhorar o onboarding ou oferecer templates prontos.
                      </p>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </>
        ) : (
          <EmptyState text="Ainda não há dados de cancelamento para analisar" />
        )}
      </SectionCard>

      {/* ══════════════════════════════════════════
          10. PERFIL DOS USUARIOS (ONBOARDING)
          ══════════════════════════════════════════ */}
      <div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">Quem são seus usuários?</h3>
        <p className="text-sm text-gray-400 mb-4">Dados coletados no onboarding para entender o perfil e comportamento de cada usuário</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <OnboardingChart
            question="Qual o tipo de negócio mais comum?"
            description="Ajuda a definir para quem você está vendendo"
            icon={Briefcase}
            data={onboarding_insights?.business_type}
            color="#3b82f6"
          />

          <OnboardingChart
            question="De qual segmento eles vêm?"
            description="Entenda quais nichos mais usam a plataforma"
            icon={Target}
            data={onboarding_insights?.segment}
            color="#8b5cf6"
          />

          <OnboardingChart
            question="Em quais plataformas publicam?"
            description="Onde seus usuários mais precisam de conteúdo"
            icon={Globe}
            data={onboarding_insights?.main_platforms}
            color="#10b981"
          />

          <SectionCard
            question="Qual o nível de marketing dos usuários?"
            description="Entenda se seus usuários são iniciantes ou experientes para adaptar a comunicação"
          >
            {onboarding_insights?.marketing_level?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={onboarding_insights.marketing_level}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={75}
                    dataKey="count"
                    nameKey="name"
                    label={renderPieLabel}
                    labelLine={false}
                  >
                    {onboarding_insights.marketing_level.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend formatter={(value) => <span className="text-xs text-gray-600">{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState text="Sem dados de onboarding" />
            )}
          </SectionCard>

          <OnboardingChart
            question="Quais temas de conteúdo mais interessam?"
            description="Use isso para criar templates e sugestões mais relevantes"
            icon={Palette}
            data={onboarding_insights?.content_themes}
            color="#f59e0b"
            className="lg:col-span-2"
          />
        </div>
      </div>

      {/* ══════════════════════════════════════════
          11. USO MEDIO
          ══════════════════════════════════════════ */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-5 border border-purple-100">
        <h3 className="text-base font-semibold text-gray-800 mb-1">Quanto conteúdo os usuários geram em média?</h3>
        <p className="text-sm text-gray-400 mb-4">Isso ajuda a entender se os limites dos planos estão adequados</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-4 border border-purple-100 flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Image className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-700">{kpi_summary?.avg_images_per_user || 0}</p>
              <p className="text-xs text-purple-400">imagens por usuário em média</p>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-blue-100 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Video className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{kpi_summary?.avg_videos_per_user || 0}</p>
              <p className="text-xs text-blue-400">vídeos por usuário em média</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// SUB-COMPONENTES
// ============================================

const KpiCard = ({ icon: Icon, question, value, suffix, color, bgColor, borderColor }) => (
  <div className={`bg-white rounded-xl p-4 border ${borderColor || 'border-gray-100'} shadow-sm`}>
    <div className="flex items-center gap-2 mb-3">
      <div className={`p-2 rounded-lg ${bgColor}`}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
    </div>
    <p className="text-xs text-gray-400 font-medium mb-1">{question}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    {suffix && <p className="text-xs text-gray-400 mt-0.5">{suffix}</p>}
  </div>
);

const OnboardingChart = ({ question, description, icon: Icon, data, color, className = '' }) => (
  <SectionCard question={question} description={description} className={className}>
    {data?.length > 0 ? (
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            width={120}
            tickFormatter={(v) => v.length > 18 ? v.slice(0, 18) + '...' : v}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="count" name="Usuários" fill={color} radius={[0, 6, 6, 0]} />
        </BarChart>
      </ResponsiveContainer>
    ) : (
      <EmptyState text="Sem dados de onboarding" />
    )}
  </SectionCard>
);

const EmptyState = ({ text }) => (
  <div className="flex items-center justify-center h-[200px] text-gray-300 text-sm">
    {text}
  </div>
);

// ============================================
// SUMARIO EXECUTIVO DINAMICO
// Calcula leitura e sugestoes a partir dos dados reais - nada hardcoded.
// ============================================

const ExecutiveSummary = ({
  kpi,
  planDistribution,
  userActivity,
  churn,
  onboardingFunnel,
  postsBeforeChurn
}) => {
  if (!kpi) return null;

  const total = kpi.total_users || 0;
  if (total === 0) return null;

  const findPlan = (name) =>
    (planDistribution || []).find(p => p.name === name)?.value || 0;

  // Fonte canonica: kpi.paid_users (plano pago + status active/trialing).
  // Fallback para soma do plan_distribution caso o backend ainda nao exponha paid_users.
  const paidActive = kpi.paid_users ?? (findPlan('Max') + findPlan('Pro') + findPlan('Basic'));
  const paymentOverdue = kpi.payment_overdue_users ?? findPlan('Pagamento em atraso');
  const canceled = findPlan('Cancelado');
  const trialExpired = findPlan('Trial expirado');
  const neverSubscribed = findPlan('Nunca assinou');

  const neverLogged =
    (userActivity?.last_login_distribution || [])
      .find(d => d.name === 'Nunca logou')?.count || 0;
  const activeLast30 =
    (userActivity?.last_login_distribution || [])
      .filter(d => ['Hoje', 'Últimos 7 dias', 'Últimos 30 dias'].includes(d.name))
      .reduce((s, d) => s + d.count, 0);

  const onbCompleted = onboardingFunnel?.completed || 0;
  const onbAbandoned = onboardingFunnel?.abandoned || 0;
  const onbNeverStarted = onboardingFunnel?.never_started || 0;

  const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);
  const fmt = (n, d = total) => (d > 0 ? Math.round((n / d) * 100) : 0);

  // Zero or one post before cancel (valor de produto)
  const zeroOrFewPostsBeforeChurn = (postsBeforeChurn?.histogram || [])
    .filter(h => h.range === '0' || h.range === '1-5')
    .reduce((s, h) => s + h.count, 0);

  // ── Sugestoes dinamicas (com threshold) ──
  const suggestions = [];

  if (paymentOverdue > 0) {
    suggestions.push({
      type: 'warning',
      title: `${paymentOverdue} ${paymentOverdue === 1 ? 'assinatura' : 'assinaturas'} com pagamento em atraso (past_due)`,
      body: `Cartão falhou no último ciclo. Stripe ainda está tentando, mas sem sucesso a assinatura cancela em poucos dias. Vale acionar esses clientes direto para atualizarem o método de pagamento antes do churn.`
    });
  }

  if (neverSubscribed > 0 && pct(neverSubscribed) >= 20) {
    suggestions.push({
      type: 'warning',
      title: `${neverSubscribed} leads criaram conta mas não completaram assinatura (${pct(neverSubscribed)}%)`,
      body: `Stripe é obrigatório no checkout, mas o trial de 7 dias pode começar sem cartão. Esses usuários abandonaram antes de inserir método de pagamento. Email de recuperação no 1º e 3º dia pode resgatar parte desse grupo.`
    });
  }

  if (canceled > 0 && paidActive > 0 && canceled >= paidActive) {
    suggestions.push({
      type: 'danger',
      title: `Cancelamentos (${canceled}) igualam ou superam pagantes ativos (${paidActive})`,
      body: `Churn acumulado está alto. Vale investigar por pesquisa de saída e cruzar com "posts antes de sair" abaixo para entender se é problema de valor percebido ou dificuldade de uso.`
    });
  }

  if (zeroOrFewPostsBeforeChurn > 0 && canceled > 0 &&
      fmt(zeroOrFewPostsBeforeChurn, canceled) >= 50) {
    suggestions.push({
      type: 'warning',
      title: `${fmt(zeroOrFewPostsBeforeChurn, canceled)}% dos cancelados geraram 5 posts ou menos antes de sair`,
      body: `Usuário não chegou a ver valor antes de cancelar. Considere melhorar onboarding, oferecer templates prontos ou reforçar o fluxo de primeiro uso.`
    });
  }

  if (neverLogged > 0 && pct(neverLogged) >= 40) {
    suggestions.push({
      type: 'info',
      title: `${pct(neverLogged)}% sem atividade detectada após signup`,
      body: `Inclui quem criou conta e não voltou, e quem entrou pouco. "Atividade" agora considera logins E geração de conteúdo. Email de boas-vindas com call-to-action pode ativar parte desse grupo.`
    });
  }

  if (onbNeverStarted > 0 && fmt(onbNeverStarted, total) >= 30) {
    suggestions.push({
      type: 'info',
      title: `${fmt(onbNeverStarted, total)}% nunca iniciou o onboarding`,
      body: `Esse funil define o perfil do usuário (segmento, plataformas, temas). Sem ele, a geração de posts fica genérica. Tornar o onboarding obrigatório ou mais incentivado deve aumentar retenção.`
    });
  }

  const conversionRate = kpi.conversion_rate || 0;
  if (conversionRate > 0 && conversionRate < 20 && total >= 20) {
    suggestions.push({
      type: 'info',
      title: `Conversão de ${conversionRate}% de signups para pagante ativo`,
      body: `Para SaaS B2C com trial grátis, 15–25% é faixa comum. Abaixo disso, os gargalos costumam ser: checkout com cartão, valor percebido nos primeiros 7 dias, ou abandono no meio do trial.`
    });
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 border border-gray-200 shadow-sm">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2.5 bg-white rounded-xl border border-gray-200">
          <Activity className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Leitura da base — o que esses números realmente dizem</h2>
          <p className="text-sm text-gray-500">Resumo automático calculado a partir dos dados atuais</p>
        </div>
      </div>

      {/* Linha 1: leitura numerica da base */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryPill
          label="Pagantes ativos"
          value={paidActive}
          pct={pct(paidActive)}
          color="emerald"
          subtitle={paymentOverdue > 0
            ? `Basic/Pro/Max em dia (+${paymentOverdue} em atraso)`
            : 'Basic + Pro + Max em active/trialing'}
        />
        <SummaryPill
          label="Ex-pagantes"
          value={canceled}
          pct={pct(canceled)}
          color="red"
          subtitle="Cancelaram após ter assinado"
        />
        <SummaryPill
          label="Nunca assinou"
          value={neverSubscribed + trialExpired}
          pct={pct(neverSubscribed + trialExpired)}
          color="amber"
          subtitle="Criou conta, não completou pagamento"
        />
        <SummaryPill
          label="Com atividade (30d)"
          value={activeLast30}
          pct={pct(activeLast30)}
          color="blue"
          subtitle="Login ou post nos últimos 30 dias"
        />
      </div>

      {/* Linha 2: narrativa em texto */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 mb-5">
        <p className="text-sm text-gray-700 leading-relaxed">
          Sua base tem <strong>{total} usuários</strong>.{' '}
          {paidActive > 0 && (
            <>
              <strong className="text-emerald-700">{paidActive}</strong> estão pagando ou em trial ativo
              {' '}({pct(paidActive)}%).{' '}
            </>
          )}
          {canceled > 0 && (
            <>
              <strong className="text-red-600">{canceled}</strong> já cancelaram
              {' '}({pct(canceled)}% — churn de {kpi.churn_rate}%).{' '}
            </>
          )}
          {(neverSubscribed + trialExpired) > 0 && (
            <>
              <strong className="text-amber-600">{neverSubscribed + trialExpired}</strong>{' '}
              criaram conta mas não chegaram a completar uma assinatura paga ({pct(neverSubscribed + trialExpired)}%) —
              isso é normal já que o Stripe permite iniciar trial de 7 dias sem cartão.{' '}
            </>
          )}
          {onbCompleted > 0 && (
            <>
              Do total, <strong>{onbCompleted}</strong> completaram o onboarding
              {' '}({fmt(onbCompleted, total)}%) e{' '}
              <strong>{onbAbandoned + onbNeverStarted}</strong> não passaram dessa etapa.
            </>
          )}
        </p>
      </div>

      {/* Linha 3: sugestoes dinamicas */}
      {suggestions.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Pontos de atenção e oportunidades
          </p>
          <div className="space-y-2">
            {suggestions.map((s, i) => (
              <SuggestionItem key={i} type={s.type} title={s.title} body={s.body} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryPill = ({ label, value, pct, subtitle, color }) => {
  const palette = {
    emerald: { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
    red:     { text: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-200' },
    amber:   { text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200' },
    blue:    { text: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-200' }
  }[color] || { text: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200' };
  return (
    <div className={`${palette.bg} ${palette.border} rounded-xl p-3 border`}>
      <div className="flex items-baseline gap-2">
        <p className={`text-2xl font-bold ${palette.text}`}>{value}</p>
        <p className={`text-xs font-medium ${palette.text} opacity-70`}>{pct}%</p>
      </div>
      <p className="text-xs font-semibold text-gray-700 mt-1">{label}</p>
      {subtitle && <p className="text-[11px] text-gray-400 mt-0.5 leading-tight">{subtitle}</p>}
    </div>
  );
};

const SuggestionItem = ({ type, title, body }) => {
  const palette = {
    danger:  { icon: AlertTriangle, bg: 'bg-red-50',    border: 'border-red-200',    iconColor: 'text-red-500',    title: 'text-red-800',    body: 'text-red-700' },
    warning: { icon: AlertTriangle, bg: 'bg-amber-50',  border: 'border-amber-200',  iconColor: 'text-amber-500',  title: 'text-amber-800',  body: 'text-amber-700' },
    info:    { icon: ArrowRight,    bg: 'bg-blue-50',   border: 'border-blue-200',   iconColor: 'text-blue-500',   title: 'text-blue-800',   body: 'text-blue-700' }
  }[type] || { icon: ArrowRight, bg: 'bg-gray-50', border: 'border-gray-200', iconColor: 'text-gray-500', title: 'text-gray-800', body: 'text-gray-600' };
  const Icon = palette.icon;
  return (
    <div className={`${palette.bg} ${palette.border} rounded-xl p-3 border flex items-start gap-3`}>
      <Icon className={`w-4 h-4 ${palette.iconColor} mt-0.5 shrink-0`} />
      <div className="min-w-0">
        <p className={`text-sm font-semibold ${palette.title}`}>{title}</p>
        <p className={`text-xs ${palette.body} mt-1 leading-relaxed`}>{body}</p>
      </div>
    </div>
  );
};

export default UsersAnalyticsDashboard;
